import './draw.scss';

import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';

import throttle from 'lodash.throttle';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import * as canvasConstants from './constants/canvas';
import * as signatureTypes from './constants/signature-types';
import { canvasReset, canvasStart, canvasStop } from './contexts/canvas/actions';
import { withCanvasContext } from './contexts/canvas';

const messages = defineMessages({
  clear: {
    id: '',
    description: 'Text for a button which clears all input when pressed',
    defaultMessage: 'Clear',
  },
  undo: {
    id: '',
    description: 'Text for a button which undos the previous action when pressed.',
    defaultMessage: 'Undo',
  },
  redo: {
    id: '',
    description: 'Text for a button which redos the previous action when pressed.',
    defaultMessage: 'Redo',
  },
});

/**
 * @typedef {Object} coord
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Object} Dimensions
 * @property {number} x
 * @property {number} y
 * @property {number} width
 * @property {number} height
 */

class Canvas extends React.PureComponent {

  state = {
    currentRevisionIndex: -1,
    revisions: [],
  };

  /**
   * The current minimum x-coordinate of the signature
   * drawn by the user. This value will be used to
   * calculate an invisible bounding box surrounding the
   * signature so that if the viewport is resized we can
   * properly reposition and scale the signature.
   *
   * @type {?number}
   */
  xMin = null;

  /**
   * The current maximum x-coordinate of the signature
   * drawn by the user. This value will be used to
   * calculate an invisible bounding box surrounding the
   * signature so that if the viewport is resized we can
   * properly reposition and scale the signature.
   *
   * @type {?number}
   */
  xMax = null;

  /**
   * The current minimum y-coordinate of the signature
   * drawn by the user. This value will be used to
   * calculate an invisible bounding box surrounding the
   * signature so that if the viewport is resized we can
   * properly reposition and scale the signature.
   *
   * @type {?number}
   */
  yMin = null;

  /**
   * The current maximum y-coordinate of the signature
   * drawn by the user. This value will be used to
   * calculate an invisible bounding box surrounding the
   * signature so that if the viewport is resized we can
   * properly reposition and scale the signature.
   *
   * @type {?number}
   */
  yMax = null;

  /**
   * A React reference to the signature canvas element.
   *
   * @type {ReactRef}
   */
  drawCanvasRef = React.createRef();

  /**
   * Called when the clear button is pressed. Resets the
   * signature canvas.
   */
  onClearButtonPressed = () => {
    this.resetCanvas();
  };

  /**
   * Called when the undo button is pressed.
   */
  onUndoButtonPressed = () => {
    this.undo();
  };

  /**
   * Called when the redo button is pressed.
   */
  onRedoButtonPressed = () => {
    this.redo();
  };

  /**
   * Handles jQuery.canvas events.
   *
   * @param {string} evtName
   * @param {Coord} point
   */
  onCanvasEvent = (evtName, coord) => {
    const { dispatch } = this.props.canvasContext;

    switch (evtName) {
      case canvasConstants.START:
        dispatch(canvasStart());
        this.calculateSignatureBoundingBox(coord);
        break;
      case canvasConstants.DRAG:
        this.calculateSignatureBoundingBox(coord);
        break;
      case canvasConstants.STOP:
        dispatch(canvasStop());
        this.saveCanvasRevision();
        break;
      default:
        // No default.
    }
  };

  /**
   * Called when the mouse is moved within the document.
   * If the user is dragging while moving their mouse (i.e.
   * drawing their signature), we don't want the browser
   * to try to scroll the document so we prevent the
   * default action and stop the event propagation.
   */
  onMouseMove = (evt) => {
    if (this.props.canvasContext.state.isDrawing) {
      evt.stopPropagation();
      evt.preventDefault();
    }
  };

  /**
   * Called when the window is resized. Redraws the canvas
   * and updates the canvas area dimensions.
   */
  onWindowResize = () => {
    this.redrawCanvas();
    this.updateCanvasDimensions();
  };

  /**
   * Throttled window resize. The less often we call this
   * during window resize events, the better the JavaScript
   * performance will be and the higher the quality the
   * redrawn signature will have.
   */
  onWindowResizeThrottled = throttle(this.onWindowResize, 50);

  componentDidMount() {
    this.initCanvas();
    this.updateCanvasDimensions();

    window.addEventListener('resize', this.onWindowResizeThrottled);
    document.addEventListener('mousemove', this.onMouseMove);

    this.props.enableInsertButtonCallback(this.shouldEnableInsert());
  }

  componentWillUnmount() {
    this.props.canvasContext.dispatch(canvasReset());

    window.removeEventListener('resize', this.onWindowScrollDebounced);
    document.removeEventListener('mousemove', this.onMouseMove);
  }

  /**
   * Initializes the canvas with jQuery.canvas and updates
   * the canvas dimensions.
   *
   * NOTE: Canvas element must have an id in order for
   * $.canvas() to work.
   */
  initCanvas() {
    if (!this.drawCanvasRef.current) return;

    const canvas = this.drawCanvasRef.current;
    const $canvas = $(canvas);

    // Not initially available in Storybook
    if (typeof $canvas.canvas !== 'function') {
      return;
    }

    // Initialize jquery.canvas.
    $canvas.canvas({
      callback: this.onCanvasEvent,
    });

    this.updateCanvasDimensions();
  }

  /**
   * Resets the canvas by clearing the signature canvas
   * area snapshot and nulling any related fields, then
   * clears the canvas.
   */
  resetCanvas() {
    this.props.canvasContext.dispatch(canvasReset());


    this.clearCanvas();
    this.resetRevisions();

    this.props.enableInsertButtonCallback(false);
  }

  /**
   * Clears the revision history.
   */
  resetRevisions() {
    this.setState({
      currentRevisionIndex: -1,
      revisions: [],
    });
  }

  /**
   * Clears the canvas and nulls the xMin, xMax, yMin, and
   * yMax fields.
   */
  clearCanvas() {
    const $canvas = $(this.drawCanvasRef.current);

    this.xMin = null;
    this.xMax = null;
    this.yMin = null;
    this.yMax = null;

    $canvas.canvas('clearCanvas');
  }

  undo() {
    this.setState((state) => ({
      currentRevisionIndex: state.currentRevisionIndex - 1,
    }), () => {
      if (this.state.revisions.length >= 0) {
        this.redrawCanvas();
      } else {
        this.clearCanvas();
      }
    });
  }

  redo() {
    if (this.state.currentRevisionIndex < this.state.revisions.length - 1) {
      this.setState((state) => ({
        currentRevisionIndex: state.currentRevisionIndex + 1,
      }), () => {
        this.redrawCanvas();
      });
    }
  }

  /**
   * Caches the current signature canvas area as an image,
   * and stores the bounding box in more long-term
   * variables.
   */
  saveCanvasRevision() {
    this.props.enableInsertButtonCallback(true);

    const canvas = this.drawCanvasRef.current;
    const canvasData = this.getCanvasData();
    const isEmpty = this.xMin === null;

    const canvasSnapshot = new Image();
    canvasSnapshot.src = canvasData;

    this.setState((state) => ({
      currentRevisionIndex: state.currentRevisionIndex + 1,
      revisions: state.revisions.slice(0, state.currentRevisionIndex + 1).concat([{
        xCoord: isEmpty ? 0 : this.xMin,
        yCoord: isEmpty ? 0 : this.yMin,
        sigWidth: isEmpty ? 0 : this.xMax - this.xMin,
        sigHeight: isEmpty ? 0 : this.yMax - this.yMin,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        canvasSnapshot,
      }]),
    }));
  }

  /**
   * Redraws the canvas by first clearing it, and then
   * after a short timeout, draws the canvas with the
   * cached image data and dimensions.
   */
  redrawCanvas() {
    this.clearCanvas();

    const revision = this.getCurrentRevision();

    setTimeout(() => {
      if (revision && this.drawCanvasRef.current) {
        // If we have image data and the canvas is mounted,
        // draw the canvas.
        this.drawCanvas(revision);
      }
    }, 10);
  }

  /**
   * Draws the given image onto the canvas area. There are
   * calculations in place which in general never allow the
   * signature to be cut off or warped in any way if the
   * window is resized.
   *
   * @param {Object} revision
   * @param {number} revision.xCoord
   * @param {number} revision.yCoord
   * @param {number} revision.sigWidth
   * @param {number} revision.sigHeight
   * @param {number} revision.canvasWidth
   * @param {number} revision.canvasHeight
   * @param {string} revision.canvasSnapshot
   */
  drawCanvas({ xCoord, yCoord, sigWidth, sigHeight, canvasWidth, canvasHeight, canvasSnapshot }) {
    const canvas = this.drawCanvasRef.current;
    const ctx = this.getCanvasContext();
    const leftPad = 50;
    const rightPad = 10;

    const dWidth = Math.min(sigWidth, canvas.width - (leftPad + rightPad));
    const dHeight = (dWidth * sigHeight) / sigWidth;

    let dx = xCoord;
    let dy = yCoord;

    if (dx + dWidth > canvas.width - rightPad) {
      if (xCoord + sigWidth < canvasWidth - rightPad) {
        dx = canvas.width - dWidth - rightPad;
      }
    }

    if (xCoord + sigWidth >= canvasWidth - rightPad) {
      if (dx < xCoord) {
        dx = canvas.width - dWidth;
      }
    }

    if (dx < leftPad) {
      if (xCoord >= leftPad) {
        dx = leftPad;
      }
    }

    dy = canvas.height - (canvasHeight - (yCoord + sigHeight)) - dHeight;

    // The old way:
    // dy = yCoord + (sigHeight - dHeight);

    this.xMin = dx;
    this.xMax = dx + dWidth;
    this.yMin = dy;
    this.yMax = dy + dHeight;

    ctx.drawImage(canvasSnapshot, xCoord, yCoord, sigWidth, sigHeight, dx, dy, dWidth, dHeight);
  }

  /**
   * Updates the canvas dimensions to fit within the
   * container element.
   *
   * NOTE: Programmatically modifying the width and height
   * of an HTMLCanvasElement clears the canvas context.
   */
  updateCanvasDimensions() {
    const canvas = this.drawCanvasRef.current;

    if (canvas) {
      const parentRect = this.getCanvasContainerDimensions();

      canvas.width = parentRect.width;
      canvas.height = parentRect.height;
    }
  }

  /**
   * Calculates the bounding box of the signature currently
   * drawn by the user.
   *
   * @param {Coord} coord
   */
  calculateSignatureBoundingBox({ x, y }) {
    if (this.xMin === null || x < this.xMin) {
      this.xMin = Math.max(Math.floor(x - 1), 0); // Avoid negatives.
    }

    if (this.xMax === null || x > this.xMax) {
      this.xMax = Math.max(Math.ceil(x + 1), 0); // Avoid negatives.
    }

    if (this.yMin === null || y < this.yMin) {
      this.yMin = Math.max(Math.floor(y - 1), 0); // Avoid negatives.
    }

    if (this.yMax === null || y > this.yMax) {
      this.yMax = Math.max(Math.ceil(y + 1), 0); // Avoid negatives.
    }
  }

  getCurrentRevision() {
    return this.state.revisions[this.state.currentRevisionIndex];
  }

  /**
   * Returns the canvas data as a high quality image/png
   * data URI.
   *
   * @returns {string}
   */
  getCanvasData() {
    return this.drawCanvasRef.current.toDataURL('image/png', 1);
  }

  /**
   * Returns the 2d canvas drawing context.
   *
   * @returns {CanvasRenderingContext2D}
   */
  getCanvasContext() {
    return this.drawCanvasRef.current.getContext('2d', {
      alpha: false,
    });
  }

  /**
   * Gets the bounding client rect of the canvas container.
   *
   * @returns {DOMRect}
   */
  getCanvasContainerDimensions() {
    const canvas = this.drawCanvasRef.current;
    const parentRect = canvas.parentElement.getBoundingClientRect();

    return parentRect;
  }

  /**
   * @returns {Object}
   * @public
   */
  getSignatureData() {
    return {
      create_type_code: signatureTypes.CANVAS,
      signature: {
        image: this.getCanvasData(),
        is_vml: 0,
      },
    };
  }

  /**
   * @returns {boolean}
   * @public
   */
  shouldEnableInsert() {
    return this.props.canvasContext.state.hasDrawn;
  }

  render() {
    // const { currentRevisionIndex, revisions } = this.state;
    const { intl, canvasContext } = this.props;
    const { hasDrawn } = canvasContext.state;

    return (
      <div className="signature-modal-draw">
        <div className="signature-modal-draw__canvas-container">
          <canvas
            id="signature-modal-draw__canvas"
            className="signature-modal-draw__canvas"
            ref={this.drawCanvasRef}
            data-qa-ref="signing-modal--draw-canvas" />
          <div className="signature-modal-draw__line" />
        </div>
        <div className="signature-modal-draw__buttons">
          {hasDrawn && (
            <Button
              kind={ButtonType.Subtle}
              buttonClass="signature-modal-draw__button signature-modal-draw__button--clear"
              data-qa-ref="signing-modal--clear"
              onClick={this.onClearButtonPressed}
            >
              {intl.formatMessage(messages.clear)}
            </Button>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(withCanvasContext(Canvas), { forwardRef: true });
